import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trophy, Target, ChartBar, Broadcast, Watch, WhatsappLogo, ShareNetwork, LinkSimple, InstagramLogo, QrCode, DownloadSimple } from "@phosphor-icons/react";
import DydProfileBlock from "@/components/DydProfileBlock";

const API = process.env.REACT_APP_BACKEND_URL;
const DEVICE_LABEL = { apple: "Apple Watch", suunto: "Suunto" };

function setMeta(prop, content, attr = "property") {
  let el = document.querySelector(`meta[${attr}="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, prop); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function PublicPlayer() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/player/${userId}`).then((r) => r.ok ? r.json() : Promise.reject(r)).then(setData).catch(() => setErr(true));
    setMeta("og:image", `${API}/api/public/player/${userId}/og.png`);
    setMeta("og:type", "profile");
    setMeta("og:url", window.location.href);
  }, [userId]);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.name} · Disfruta y Diviertete`;
    setMeta("og:title", `${data.name} · Ranking #${data.stats.ranking}`);
    setMeta("og:description", `${data.stats.points} pts · ${data.stats.games_won} victorias · ${data.validated_matches_count} partidos validados`);
    setMeta("description", `Perfil publico de ${data.name} en Disfruta y Diviertete Padel`, "name");
  }, [data]);

  if (err) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-gray-400">Jugador no encontrado</div>;
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  const shareUrl = window.location.href;
  const shareText = `${data.name} · #${data.stats.ranking} en DyD Padel · ${data.stats.points} pts`;
  const copy = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Enlace copiado"); } catch { toast.error("No se pudo copiar"); } };
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank");
  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const shareNative = async () => {
    if (navigator.share) { try { await navigator.share({ title: data.name, text: shareText, url: shareUrl }); } catch { /* user dismissed */ } }
    else copy();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6 sm:p-10" data-testid="public-player-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <Link to="/" className="inline-block text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] hover:text-white transition-colors">DISFRUTA Y DIVIERTETE · PADEL</Link>

        <div className="relative overflow-hidden bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-white/10 p-8 sm:p-10" data-testid="public-card">
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#CCFF00]" />
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-2">{data.role?.toUpperCase()}{data.club ? ` · ${data.club}` : ""}</p>
              <h1 className="font-['Barlow_Condensed'] text-5xl sm:text-6xl font-black uppercase tracking-tighter">{data.name}</h1>
              <div className="flex gap-2 mt-3 flex-wrap">
                {data.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 uppercase" data-testid="public-category">Cat. {data.category}</Badge>}
                {data.experience_years != null && <Badge variant="outline" className="bg-white/5 text-gray-300 border-white/10 uppercase">{data.experience_years} anos</Badge>}
                {data.state && data.country && <Badge variant="outline" className="bg-white/5 text-gray-300 border-white/10 uppercase">{data.state}, {data.country}</Badge>}
                {(data.linked_devices || []).map((k) => (
                  <Badge key={k} variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 uppercase" data-testid={`public-device-${k}`}>
                    <Watch size={12} className="inline mr-1" />{DEVICE_LABEL[k] || k}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="text-right">
              <p className="font-['Barlow_Condensed'] text-6xl sm:text-7xl font-black text-[#CCFF00] leading-none" data-testid="public-ranking">#{data.stats.ranking}</p>
              <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">Ranking global</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-8 border-t border-white/5">
            {[
              { v: data.stats.points, l: "Puntos", i: Target },
              { v: data.stats.games_won, l: "Victorias", i: Trophy },
              { v: data.stats.games_played, l: "Partidos", i: ChartBar },
              { v: data.validated_matches_count, l: "Marcador", i: Broadcast },
            ].map((s) => (
              <div key={s.l}>
                <s.i size={18} weight="duotone" className="text-[#CCFF00] mb-1" />
                <p className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black text-white">{s.v}</p>
                <p className="text-[10px] uppercase tracking-widest text-gray-500">{s.l}</p>
              </div>
            ))}
          </div>

          {data.top_rivals?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-white/5">
              <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Rivales mas frecuentes</p>
              <div className="flex gap-2 flex-wrap">
                {data.top_rivals.map((r, i) => (
                  <div key={i} className="bg-[#0A0A0A] border border-white/10 px-3 py-2">
                    <p className="text-sm font-semibold text-white">{r.name}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">{r.count} {r.count === 1 ? "partido" : "partidos"}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DydProfileBlock userId={userId} />

        <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
          <Button onClick={shareWA} className="bg-green-500 text-white hover:bg-green-600 uppercase text-xs font-bold" data-testid="share-whatsapp"><WhatsappLogo size={14} className="mr-1" />WhatsApp</Button>
          <Button onClick={shareX} variant="outline" className="border-white/20 text-gray-300 hover:text-white uppercase text-xs font-bold" data-testid="share-x"><ShareNetwork size={14} className="mr-1" />X / Twitter</Button>
          <Button onClick={() => window.open(`https://www.instagram.com/`, "_blank")} variant="outline" className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10 uppercase text-xs font-bold" data-testid="share-instagram"><InstagramLogo size={14} className="mr-1" />Instagram</Button>
          <Button onClick={copy} variant="outline" className="border-white/20 text-gray-300 hover:text-white uppercase text-xs font-bold" data-testid="share-copy"><LinkSimple size={14} className="mr-1" />Copiar enlace</Button>
          <Button onClick={() => setQrOpen((v) => !v)} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-xs font-bold" data-testid="toggle-qr-btn"><QrCode size={14} className="mr-1" />{qrOpen ? "Ocultar QR" : "Ver QR"}</Button>
          <Button onClick={shareNative} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-xs font-bold ml-auto" data-testid="share-native"><ShareNetwork size={14} className="mr-1" />Compartir</Button>
        </div>

        {qrOpen && (
          <div className="bg-[#0A0A0A] border border-white/10 p-6 flex flex-col items-center gap-3" data-testid="qr-block">
            <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Escanea para ver mi perfil</p>
            <img src={`${API}/api/public/player/${userId}/qr.png`} alt="QR del perfil" className="w-56 h-56 bg-[#0A0A0A]" data-testid="qr-image" />
            <a href={`${API}/api/public/player/${userId}/qr.png`} download={`dyd-${data.name}.png`} className="inline-flex items-center gap-1 text-xs text-[#CCFF00] hover:underline uppercase tracking-wider" data-testid="qr-download">
              <DownloadSimple size={14} /> Descargar PNG
            </a>
            <p className="text-[11px] text-gray-500 text-center max-w-xs">Imprime este QR en tu pulsera o tarjeta del club. Al escanearlo, cualquiera ve tu ranking, puntos y partidos validados.</p>
          </div>
        )}

        <p className="text-xs text-gray-600 text-center pt-6">
          <Link to="/register" className="text-[#CCFF00] hover:underline uppercase tracking-wider">Registrate gratis</Link> y reta al campeon
        </p>
      </div>
    </div>
  );
}
